/**
 * DownloadConfirmModal — prompts the user to confirm before downloading an attachment.
 *
 * Uses Radix AlertDialog for accessible modal behavior. The "Download" button
 * triggers the actual download inside a synchronous click handler, which avoids
 * pop-up blocker issues that plague window.open in async callbacks.
 */

import React, { useState } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from './ui/alert-dialog';

export interface DownloadTarget {
  /** Display filename shown in the modal. */
  readonly filename: string;
  /** File size in bytes (optional — displayed when available). */
  readonly size?: number;
  /** MIME type (optional — displayed as file type hint). */
  readonly mimeType?: string;
}

export interface DownloadConfirmModalProps {
  /** Whether the modal is open. */
  readonly open: boolean;
  /** Called when the modal should close (cancel or after download). */
  readonly onClose: () => void;
  /** Called when the user confirms the download. */
  readonly onConfirm: () => Promise<void> | void;
  /** Attachment metadata to display. */
  readonly target: DownloadTarget | null;
}

/**
 * Formats bytes into a human-readable string.
 */
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Returns a user-friendly file type label from a MIME type.
 */
function formatFileType(mimeType: string): string {
  if (mimeType.startsWith('image/')) return 'Image';
  if (mimeType.startsWith('video/')) return 'Video';
  if (mimeType.startsWith('audio/')) return 'Audio';
  if (mimeType === 'application/pdf') return 'PDF';
  if (mimeType.startsWith('text/')) return 'Text file';
  if (mimeType.includes('spreadsheet') || mimeType.includes('ms-excel')) return 'Spreadsheet';
  if (mimeType.includes('presentation') || mimeType.includes('ms-powerpoint')) return 'Presentation';
  if (mimeType.includes('document') || mimeType.includes('msword')) return 'Document';
  if (mimeType === 'application/zip') return 'ZIP archive';
  return 'File';
}

export function DownloadConfirmModal({
  open,
  onClose,
  onConfirm,
  target,
}: DownloadConfirmModalProps): React.ReactElement {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setDownloading(true);
    setError(null);
    try {
      await onConfirm();
      onClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Download failed. Please try again.';
      setError(message);
    } finally {
      setDownloading(false);
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen && !downloading) {
      setError(null);
      onClose();
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Download attachment</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2">
              <p>
                Do you want to download this file?
              </p>
              {target && (
                <div className="rounded-md border border-border bg-muted/50 p-3 text-sm">
                  <p className="font-medium text-foreground truncate">{target.filename}</p>
                  <div className="flex gap-3 mt-1 text-muted-foreground">
                    {target.mimeType && (
                      <span>{formatFileType(target.mimeType)}</span>
                    )}
                    {target.size != null && target.size > 0 && (
                      <span>{formatSize(target.size)}</span>
                    )}
                  </div>
                </div>
              )}
              {error && (
                <p className="text-sm text-destructive" role="alert">
                  {error}
                </p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={downloading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={downloading}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {downloading ? 'Downloading...' : 'Download'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
